import * as AppConstants from '../constants/app';
import { User } from '../../interfaces/user';
import { ClientInfo } from '../../interfaces/clients';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';
import { IMUser } from '../../interfaces/im-user';
import invoiceManagerService from '../../services/invoice-manager.service';
import financialService from '../../services/financial.service';
import Cookies from 'universal-cookie';
import type { RootState } from 'bootstrap';
import type { CustomerAlertBoxState } from 'redux/reducers/appReducer';

const globalAlertCookie = 'hideCustomerAlertBox';

export function setUser(user: User) {
  LocalAuth.setUser(user as any);
  return { type: AppConstants.SET_USER, user };
}

export function setLoginDetails(details: { email: string }) {
  return { type: AppConstants.SET_LOGIN_DETAILS, details };
}

export const getLoginDetails = (state: RootState) => {
  return state.app.loginDetails;
};

export function setActiveAccountInfo(activeAccountInfo: ClientInfo | Partial<ClientInfo>) {
  return { type: AppConstants.SET_ACTIVE_ACCOUNT_INFO, activeAccountInfo };
}

export const getActiveAccountInfo = (state: RootState): ClientInfo => {
  return state.app.activeAccountInfo;
};

export function setIMUser(user: IMUser) {
  return { type: AppConstants.SET_IM_USER, user };
}

export const getUser = (state: RootState): User => {
  return state.app.user;
};

export const getIMUser = (state: RootState): IMUser => {
  return state.app.imUser;
};

export const getCadenceAccounts = (state: RootState): any => {
  return state.app.cadenceAccounts;
};

export const getCustomerAlertBox = (state: RootState): CustomerAlertBoxState => {
  return state.app.customerAlertBox;
};

export const getLoadingCadenceAccounts = (state: RootState): any => {
  return state.app.loadingAccounts;
};

export function addClientName(addedCadenceName) {
  return { type: AppConstants.ADD_ACCOUNT_NAME, addedCadenceName };
}

export function setCadenceAccounts(cadenceAccounts) {
  return { type: AppConstants.SET_CADENCE_ACCOUNTS, cadenceAccounts };
}

export function setCustomerAlertBox(customerAlertBox) {
  return { type: AppConstants.SET_CUSTOMER_ALERT_BOX, customerAlertBox };
}

export function setLoadingCadenceAccounts(loadingAccounts) {
  return { type: AppConstants.SET_LOADING_ACCOUNTS, loadingAccounts };
}

export function setProductTeaser(productTeaser) {
  return { type: AppConstants.SET_PRODUCT_TEASER, productTeaser };
}

export function setDismissCookie() {
  return async function (dispatch, getState) {
    const customerAlertBox = getState().app.customerAlertBox;
    const cookies = new Cookies();
    cookies.set(globalAlertCookie, true, { maxAge: 900 });
    dispatch(setCustomerAlertBox({ ...customerAlertBox, visible: false }));
  };
}

export function fetchCadenceAccounts(clientNumbers: Array<string>, imUser: IMUser) {
  return async (dispatch) => {
    if (clientNumbers && clientNumbers.length > 0) {
      dispatch(setLoadingCadenceAccounts(true));
      const fetchedAccounts = await financialService.getAccounts(clientNumbers);
      const activeAccountInformation = fetchedAccounts.find((acct) => acct.client_number === imUser?.active_account);
      const cadenceAccounts = fetchedAccounts.filter((accountInfo) =>
        imUser.accounts?.some((imUserAccount) => accountInfo.client_number === imUserAccount.reference_id),
      );
      dispatch(
        setCadenceAccounts(
          cadenceAccounts.map((account) => {
            return {
              client_number: account.client_number,
              client_name: account.name ? account.name : 'ERROR GETTING CLIENT NAME',
              name: account.name ? account.name : 'ERROR GETTING CLIENT NAME',
              mc_number: account.mc_number ? account.mc_number : '',
            };
          }),
        ),
      );
      dispatch(setActiveAccountInfo(activeAccountInformation));
      dispatch(setLoadingCadenceAccounts(false));
    } else {
      dispatch(setCadenceAccounts([]));
    }
  };
}

export function addCadenceAccount(cadenceClient) {
  return async (dispatch, getState) => {
    const imUser = getState().app.imUser as IMUser;
    const currentCadenceAccounts = imUser.accounts.map((account) => {
      return account.reference_id;
    });
    if (currentCadenceAccounts.includes(cadenceClient)) {
      return;
    }

    const updatedUser = await invoiceManagerService.addUserAccount(cadenceClient, imUser.user_id);
    const stateUser = {
      ...imUser,
      active_account: updatedUser.active_account,
      accounts: updatedUser.accounts,
    } as IMUser;
    dispatch(fetchCadenceAccounts([...currentCadenceAccounts, cadenceClient], stateUser));
    dispatch(setIMUser(stateUser));
  };
}

export function removeCadenceAccount(cadenceClientToRemove) {
  return async (dispatch, getState) => {
    const imUser = getState().app.imUser as IMUser;
    const currentCadenceAccounts = imUser.accounts.slice();
    const updatedCadenceAccounts = currentCadenceAccounts
      .filter((account) => account.reference_id !== cadenceClientToRemove)
      .map((account) => {
        return account.reference_id;
      });

    let newActiveAccount = imUser.active_account;

    if (cadenceClientToRemove === imUser.active_account) {
      newActiveAccount = updatedCadenceAccounts.length > 0 ? updatedCadenceAccounts[0] : null;
    }

    const updatedUser = await invoiceManagerService.removeUserAccount(
      cadenceClientToRemove,
      imUser.user_id,
      newActiveAccount,
    );
    const stateUser = {
      ...imUser,
      active_account: updatedUser.active_account,
      accounts: updatedUser.accounts,
    } as IMUser;
    dispatch(fetchCadenceAccounts(updatedCadenceAccounts, stateUser));
    dispatch(setIMUser(stateUser));
  };
}

export function setFeatures(features: any) {
  return { type: AppConstants.SET_FEATURES, features };
}

export function setClientIp(clientIp: string) {
  return { type: AppConstants.SET_CLIENT_IP, clientIp };
}

export const getClientIp = (state: RootState): string => {
  return state.app.clientIp;
};

export function setDeviceId(deviceId: string) {
  return { type: AppConstants.SET_DEVICE_ID, deviceId };
}

export const getDeviceId = (state: RootState): string => {
  return state.app.deviceId;
};

export function setIsSideNavigationCollapsed(isSideNavigationCollapsed: boolean) {
  return { type: AppConstants.SET_IS_SIDE_NAVIGATION_COLLAPSED, isSideNavigationCollapsed };
}

export const getIsSideNavigationCollapsed = (state: RootState): boolean => {
  return state.app.isSideNavigationCollapsed;
};
